import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { setTokenGetter, auth as authApi } from './api';

// ─── Config ──────────────────────────────────────────────────
const POOL_DATA = {
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || '',
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || '',
};

const userPool = POOL_DATA.UserPoolId ? new CognitoUserPool(POOL_DATA) : null;

// ─── Types ───────────────────────────────────────────────────
export interface AuthUser {
  sub: string;
  email: string;
  username: string;
  idToken: string;
  accessToken: string;
  isAdmin: boolean;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (email: string, code: string) => Promise<void>;
  signOut: () => void;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  getIdToken: () => Promise<string>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Helper: extract user from session ───────────────────────
function userFromSession(session: CognitoUserSession, cognitoUser: CognitoUser): AuthUser {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    sub: payload.sub as string,
    email: (payload.email as string) || '',
    username: cognitoUser.getUsername(),
    idToken: idToken.getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    isAdmin: false,
  };
}

// ─── Provider ────────────────────────────────────────────────
export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    if (!userPool) {
      // Dev mode — inject mock user matching the API's dev bypass (auth.ts)
      const devUser: AuthUser = {
        sub: 'dev-user-123',
        email: 'dev@example.com',
        username: 'developer',
        idToken: 'dev-token',
        accessToken: 'dev-token',
        isAdmin: false,
      };
      setUser(devUser);
      // Fetch admin status from API
      authApi.me().then((res) => {
        if (res.data?.isAdmin) setUser((prev) => prev ? { ...prev, isAdmin: true } : prev);
      }).catch(() => {});
      setLoading(false);
      return;
    }
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setLoading(false);
      return;
    }
    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (!err && session?.isValid()) {
        setUser(userFromSession(session, cognitoUser));
        // Fetch admin status from API
        authApi.me().then((res) => {
          if (res.data?.isAdmin) setUser((prev) => prev ? { ...prev, isAdmin: true } : prev);
        }).catch(() => {});
      }
      setLoading(false);
    });
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    if (!userPool) throw new Error('Auth not configured');
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.authenticateUser(authDetails, {
        onSuccess(session) {
          setUser(userFromSession(session, cognitoUser));
          // Fetch admin status from API
          authApi.me().then((res) => {
            if (res.data?.isAdmin) setUser((prev) => prev ? { ...prev, isAdmin: true } : prev);
          }).catch(() => {});
          resolve();
        },
        onFailure(err) {
          reject(err);
        },
      });
    });
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    if (!userPool) throw new Error('Auth not configured');
    const attrs = [new CognitoUserAttribute({ Name: 'email', Value: email })];
    return new Promise<void>((resolve, reject) => {
      userPool.signUp(email, password, attrs, [], (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  }, []);

  const confirmSignUp = useCallback(async (email: string, code: string) => {
    if (!userPool) throw new Error('Auth not configured');
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmRegistration(code, true, (err) => {
        if (err) return reject(err);
        resolve();
      });
    });
  }, []);

  const signOut = useCallback(() => {
    if (!userPool) return;
    const cognitoUser = userPool.getCurrentUser();
    cognitoUser?.signOut();
    setUser(null);
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    if (!userPool) throw new Error('Auth not configured');
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess() { resolve(); },
        onFailure(err) { reject(err); },
      });
    });
  }, []);

  const confirmForgotPassword = useCallback(async (email: string, code: string, newPassword: string) => {
    if (!userPool) throw new Error('Auth not configured');
    const cognitoUser = new CognitoUser({ Username: email, Pool: userPool });
    return new Promise<void>((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess() { resolve(); },
        onFailure(err) { reject(err); },
      });
    });
  }, []);

  const getIdToken = useCallback(async (): Promise<string> => {
    if (!userPool) return 'dev-token';
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) throw new Error('Not authenticated');
    return new Promise((resolve, reject) => {
      cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session?.isValid()) return reject(err || new Error('Session invalid'));
        const sessionUser = userFromSession(session, cognitoUser);
        setUser((prev) => prev ? { ...sessionUser, isAdmin: prev.isAdmin } : sessionUser);
        resolve(session.getIdToken().getJwtToken());
      });
    });
  }, []);

  // Wire the API token getter synchronously during render (not in useEffect)
  // so it's available before any child component fires a TanStack Query fetch.
  setTokenGetter(getIdToken);

  return (
    <AuthContext.Provider
      value={{ user, loading, signIn, signUp, confirmSignUp, signOut, forgotPassword, confirmForgotPassword, getIdToken }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be within AuthProvider');
  return ctx;
}
